import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { scrollToId } from "../lib/scroll";

const HEADLINE_LINES = [
  "Siti web professionali,",
  "moderni e progettati",
  "per la tua attività.",
];

// Mini-sito dimostrativo composto in CSS: nessuna immagine di repertorio.
const HeroMockup = () => (
  <div className="overflow-hidden rounded-xl border border-white/10 bg-white shadow-[0_40px_90px_-30px_rgba(0,0,0,0.55)]">
    <div className="flex items-center gap-2 border-b border-line bg-mist px-4 py-2.5">
      <span className="flex gap-1.5" aria-hidden="true">
        <span className="h-2.5 w-2.5 rounded-full bg-[#E3B0A6]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#E8D5A3]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#A9CEB4]" />
      </span>
      <span className="ml-2 flex-1 truncate rounded-md border border-line bg-white px-3 py-1 text-[11px] text-steel">
        www.latuaattivita.it
      </span>
    </div>
    <div className="bg-white p-6 sm:p-8">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="h-6 w-6 rounded-md bg-navy-dark" aria-hidden="true" />
          <span className="h-2 w-16 rounded-full bg-navy-dark/80" aria-hidden="true" />
        </div>
        <div className="flex items-center gap-3" aria-hidden="true">
          <span className="h-1.5 w-8 rounded-full bg-line" />
          <span className="h-1.5 w-8 rounded-full bg-line" />
          <span className="h-1.5 w-8 rounded-full bg-line" />
        </div>
      </div>
      <div className="mt-8 space-y-2.5" aria-hidden="true">
        <motion.span
          className="block h-3.5 w-4/5 rounded-full bg-navy"
          initial={{ scaleX: 0, originX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ delay: 0.9, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        />
        <motion.span
          className="block h-3.5 w-3/5 rounded-full bg-navy/70"
          initial={{ scaleX: 0, originX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ delay: 1.05, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        />
        <motion.span
          className="block h-2 w-2/5 rounded-full bg-steel/40"
          initial={{ scaleX: 0, originX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ delay: 1.2, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        />
      </div>
      <div className="mt-7 flex items-center gap-3">
        <motion.span
          className="rounded-lg bg-celeste px-4 py-2 text-[11px] font-semibold text-white"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.35, duration: 0.5 }}
        >
          Contattaci
        </motion.span>
        <motion.span
          className="rounded-lg border border-line px-4 py-2 text-[11px] font-semibold text-navy"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.45, duration: 0.5 }}
        >
          Scopri di più
        </motion.span>
      </div>
      <div className="mt-8 grid grid-cols-3 gap-3" aria-hidden="true">
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            className="rounded-lg border border-line bg-mist p-3"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.55 + i * 0.1, duration: 0.5 }}
          >
            <span className="block h-5 w-5 rounded-md bg-celeste/25" />
            <span className="mt-2.5 block h-1.5 w-4/5 rounded-full bg-navy/50" />
            <span className="mt-1.5 block h-1.5 w-3/5 rounded-full bg-line" />
          </motion.div>
        ))}
      </div>
    </div>
  </div>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const mockupY = useTransform(scrollYProgress, [0, 1], [0, 70]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, 140]);

  return (
    <section id="home" ref={ref} className="relative overflow-hidden bg-navy-dark text-white">
      <motion.div
        aria-hidden="true"
        style={{ y: glowY }}
        className="pointer-events-none absolute -top-40 left-1/2 h-[34rem] w-[54rem] -translate-x-1/2 rounded-full bg-celeste/10 blur-[120px]"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-white/10"
      />
      <div className="relative mx-auto grid max-w-6xl gap-14 px-5 pb-20 pt-16 sm:px-8 sm:pt-24 lg:grid-cols-12 lg:items-center lg:gap-10 lg:pb-28 lg:pt-28">
        <div className="lg:col-span-6">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-xs font-semibold uppercase tracking-[0.24em] text-celeste"
          >
            DS LAB STUDIO — Realizzazione siti web
          </motion.p>
          <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.12] tracking-tight sm:text-5xl lg:text-[3.05rem]">
            {HEADLINE_LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.15 + i * 0.13, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.65, duration: 0.6 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-white/75 sm:text-lg"
          >
            Una presenza online curata può aiutarti a farti trovare più facilmente, presentare al
            meglio la tua attività e trasformare chi ti cerca in un nuovo contatto o potenziale
            cliente.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.78, duration: 0.6 }}
            className="mt-4 max-w-xl text-sm leading-relaxed text-white/55 sm:text-base"
          >
            Mostra ciò che fai, comunica il valore della tua attività e permetti ai tuoi clienti di
            contattarti, richiedere informazioni o prenotare con facilità.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.92, duration: 0.6 }}
            className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center"
          >
            <a
              href="#contatti"
              onClick={(e) => { e.preventDefault(); scrollToId("#contatti"); }}
              data-testid="hero-primary-cta"
              className="group inline-flex items-center justify-center gap-2 rounded-lg bg-celeste px-7 py-3.5 text-base font-semibold text-white shadow-lg shadow-celeste/20 transition-all duration-200 hover:bg-celeste-dark active:scale-[0.98]"
            >
              Parlaci del tuo progetto
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden="true" />
            </a>
            <a
              href="#come-lavoriamo"
              onClick={(e) => { e.preventDefault(); scrollToId("#come-lavoriamo"); }}
              data-testid="hero-secondary-cta"
              className="inline-flex items-center justify-center rounded-lg border border-white/25 px-7 py-3.5 text-base font-semibold text-white transition-colors duration-200 hover:border-white/50 hover:bg-white/5"
            >
              Scopri come lavoriamo
            </a>
          </motion.div>
        </div>
        <motion.div
          style={{ y: mockupY }}
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-6"
        >
          <HeroMockup />
        </motion.div>
      </div>
    </section>
  );
};
